// «Aktive skiplukk» nederst på /ny-kundesamhandling/skiplukk, under kundevalget (vinnsel, 30.10):
// skiplukkene i butikken som ikke er avsluttet, eldste først, med kunde, ansvarlig/oppfølger,
// merke, modell og datoer. Hold musen over (eller tab til) en rad for detaljene per par og
// beskjeden. «Plukkliste» skriver ut samme A4-side som «Skriv ut plukkliste» i OrderDetails (plukkliste.js), uten å åpne skiplukket.
// «Skriv ut alle» (eller «Skriv ut valgte» når noen er krysset av) gir alle i ett vindu, én side hver.
// Data: GET /orders/skiplukk-aktive (bare ansatte); telefon og e-post hentes først ved utskrift.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { isAuthenticated } from '../auth';
import Ikon from '../ny-kundesamhandling/Ikon';
import { parTekst } from './felles';
import { plukklisteHtml, plukklisterHtml } from './plukkliste';
import { virkedagerTekst } from '../utils/mineOrdrer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const lesButikk = () => parseInt(Cookies.get('butikkid'), 10) || null;
const butikkNavn = (id) => (id === 2 ? 'Sport 1 Slemmestad' : id === 1 ? 'Sport 1 Røyken' : '');

// "30.10.2026, 14:22:11" → "30.10.2026"
const kortDato = (s) => (s ? String(s).split(',')[0].trim() : '');

// Merker og modeller på tvers av parene, uten like to ganger.
const unike = (par, felt) => [...new Set(par.map((p) => String(p[felt] || '').trim()).filter(Boolean))].join(', ');

async function hentKunde(kundeid) {
  if (kundeid == null) return null;
  const r = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(kundeid)}`);
  if (!r.ok) return null;
  const liste = await r.json();
  return (Array.isArray(liste) ? liste : []).find((c) => c.customerNumber === kundeid) || null;
}

function Detaljer({ id, o, par }) {
  return (
    <div
      id={id}
      className="pointer-events-none absolute left-0 right-0 top-full z-20 mt-1 hidden rounded-xl border-2 border-teal-300 bg-white p-2.5 text-sm text-slate-800 shadow-xl group-hover:block group-focus-within:block"
    >
      <ul className="space-y-1">
        {par.length ? par.map((p, i) => (
          <li key={i} className="[overflow-wrap:anywhere]">
            <span className="font-semibold">{par.length > 1 ? `Par ${i + 1}: ` : ''}</span>{parTekst(p)}
          </li>
        )) : <li>Ingen skipar fylt ut</li>}
      </ul>
      {o.Kommentar && <p className="mt-2 whitespace-pre-line [overflow-wrap:anywhere]"><span className="font-semibold">Beskjed: </span>{o.Kommentar}</p>}
      <p className="mt-2 text-slate-600">
        Status: {o.Status || 'Aktiv'}
        {o.virkedager != null && ` · ${virkedagerTekst(o.virkedager)} gammel`}
        {o.overfortTilService?.dato && ' · gjort om til service'}
      </p>
    </div>
  );
}

export default function AktiveSkiplukk() {
  const [ordrer, setOrdrer] = useState(null);
  const [feil, setFeil] = useState('');
  const [skriver, setSkriver] = useState(null);
  const [valgt, setValgt] = useState(() => new Set());

  const hent = useCallback(async () => {
    if (!isAuthenticated()) return;
    const butikk = lesButikk();
    try {
      const r = await fetch(`${API_BASE_URL}/orders/skiplukk-aktive?butikkid=${butikk || ''}&t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(String(r.status));
      setOrdrer((await r.json()).ordrer || []);
      setFeil('');
    } catch (e) {
      setFeil('Klarte ikke å hente skiplukkene.');
    }
  }, []);

  useEffect(() => {
    hent();
    window.addEventListener('storeUpdated', hent);
    return () => window.removeEventListener('storeUpdated', hent);
  }, [hent]);

  const data = (o, kunde) => ({
    ordre: o,
    kunde,
    par: o.skiplukk?.par || [],
    ansvarlig: o.Ansatt,
    oppfolger: o.Oppfolger,
    kommentar: o.Kommentar,
    butikk: butikkNavn(o.butikkid),
  });

  // Vinduet åpnes før kunden hentes, ellers stopper nettleseren det som sprettoppvindu.
  const skrivUt = async (o) => {
    const vindu = window.open('', '', 'width=900,height=1100');
    if (!vindu) {
      setFeil('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.');
      return;
    }
    setSkriver(o._id);
    try {
      const kunde = await hentKunde(o.kundeid);
      vindu.document.write(plukklisteHtml(data(o, kunde)));
      vindu.document.close();
      vindu.focus();
      vindu.onafterprint = () => vindu.close();
      vindu.print();
    } catch (e) {
      vindu.close();
      setFeil('Klarte ikke å lage plukklisten. Prøv igjen.');
    } finally {
      setSkriver(null);
    }
  };

  // Valgte som fortsatt er aktive (lista hentes på nytt når butikken byttes).
  const valgte = (ordrer || []).filter((o) => valgt.has(o._id));
  const alleValgt = ordrer?.length > 0 && valgte.length === ordrer.length;
  const velg = (id, pa) => setValgt((forrige) => {
    const ny = new Set(forrige);
    if (pa) ny.add(id); else ny.delete(id);
    return ny;
  });
  const velgAlle = (pa) => setValgt(pa ? new Set(ordrer.map((o) => o._id)) : new Set());

  // Alle i ett vindu, én side hver. Er noen krysset av, bare de. Hver kunde hentes én gang.
  const skrivUtFlere = async () => {
    const liste = valgte.length ? valgte : ordrer;
    const vindu = window.open('', '', 'width=900,height=1100');
    if (!vindu) {
      setFeil('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.');
      return;
    }
    setSkriver('flere');
    try {
      const kundeNr = [...new Set(liste.map((o) => o.kundeid).filter((n) => n != null))];
      const kunder = new Map(await Promise.all(kundeNr.map(async (nr) => [nr, await hentKunde(nr)])));
      vindu.document.write(plukklisterHtml(liste.map((o) => data(o, kunder.get(o.kundeid) || null))));
      vindu.document.close();
      vindu.focus();
      vindu.onafterprint = () => vindu.close();
      vindu.print();
    } catch (e) {
      vindu.close();
      setFeil('Klarte ikke å lage plukklistene. Prøv igjen.');
    } finally {
      setSkriver(null);
    }
  };

  if (ordrer === null && !feil) return null;

  return (
    <section aria-labelledby="nk-skiplukk" className="space-y-2">
      <h2 id="nk-skiplukk" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-600">
        <Ikon navn="snofnugg" className="w-4 h-4" /> Aktive skiplukk{ordrer?.length ? ` (${ordrer.length})` : ''}
      </h2>
      {feil && <p role="alert" className="text-sm text-rose-800">{feil}</p>}
      {ordrer && !ordrer.length && <p className="text-sm text-slate-600">Ingen aktive skiplukk i butikken.</p>}
      {ordrer?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-[2.25rem] cursor-pointer items-center gap-2 rounded-lg px-1 text-sm font-semibold text-slate-800">
            <input
              type="checkbox"
              checked={alleValgt}
              ref={(el) => { if (el) el.indeterminate = valgte.length > 0 && !alleValgt; }}
              onChange={(e) => velgAlle(e.target.checked)}
              className="h-4 w-4 accent-teal-700"
            />
            Velg alle
          </label>
          <button
            type="button"
            onClick={skrivUtFlere}
            disabled={skriver === 'flere'}
            className="inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg border-2 border-teal-300 bg-white px-3 py-1 text-sm font-semibold text-teal-900 hover:bg-teal-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 disabled:opacity-60"
          >
            <Ikon navn="dokument" className="w-4 h-4" />
            {skriver === 'flere'
              ? 'Lager plukklister …'
              : valgte.length ? `Skriv ut valgte (${valgte.length})` : `Skriv ut alle (${ordrer.length})`}
          </button>
          {valgte.length > 0 && (
            <button type="button" onClick={() => velgAlle(false)} className="rounded-lg px-2 py-1 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
              Fjern valg
            </button>
          )}
        </div>
      )}
      {ordrer?.length > 0 && (
        <ul className="space-y-1.5">
          <li aria-hidden="true" className="hidden grid-cols-[1.5rem_4rem_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_6rem_6rem_8.5rem] gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-slate-600 lg:grid">
            <span /><span>Nr.</span><span>Kunde</span><span>Ansatt</span><span>Merke</span><span>Modell</span><span>Registrert</span><span>Endret</span><span />
          </li>
          {ordrer.map((o) => {
            const par = o.skiplukk?.par || [];
            const detaljId = `skiplukk-detaljer-${o._id}`;
            const merke = unike(par, 'merke');
            const modell = unike(par, 'produkt');
            return (
              <li key={o._id} className="group relative">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border-2 border-teal-200 bg-teal-50 px-3 py-1.5 group-hover:bg-teal-100 lg:grid lg:grid-cols-[1.5rem_4rem_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_6rem_6rem_8.5rem]">
                  <input
                    type="checkbox"
                    checked={valgt.has(o._id)}
                    onChange={(e) => velg(o._id, e.target.checked)}
                    aria-label={`Velg skiplukk nr. ${o.ordreid} for utskrift`}
                    className="h-4 w-4 accent-teal-700"
                  />
                  <Link
                    to={`/order-details/${o._id}`}
                    aria-describedby={detaljId}
                    className="font-mono text-sm font-semibold text-slate-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 rounded"
                  >
                    <span className="sr-only">Skiplukk </span>Nr. {o.ordreid}
                  </Link>
                  <span className="min-w-0 basis-40 flex-1 font-semibold text-slate-900 [overflow-wrap:anywhere] lg:basis-auto">
                    {o.kundeNavn || 'Ukjent kunde'}
                    {par.length > 1 && <span className="font-normal text-slate-700"> · {par.length} par</span>}
                  </span>
                  <span className="min-w-0 text-sm text-slate-700 [overflow-wrap:anywhere]">
                    <span className="lg:sr-only">Ansatt: </span>{o.Ansatt || '–'}
                    {o.Oppfolger && o.Oppfolger !== o.Ansatt && <span className="block text-slate-600">følges opp av {o.Oppfolger}</span>}
                  </span>
                  <span className="min-w-0 text-sm text-slate-800 [overflow-wrap:anywhere]"><span className="lg:sr-only">Merke: </span>{merke || '–'}</span>
                  <span className="min-w-0 text-sm text-slate-800 [overflow-wrap:anywhere]"><span className="lg:sr-only">Modell: </span>{modell || '–'}</span>
                  <span className="text-sm text-slate-700"><span className="lg:sr-only">Registrert </span>{kortDato(o.RegistrertDato) || '–'}</span>
                  <span className="text-sm text-slate-700"><span className="lg:sr-only">Endret </span>{kortDato(o.Endretdato) || '–'}</span>
                  <button
                    type="button"
                    onClick={() => skrivUt(o)}
                    disabled={skriver === o._id}
                    className="ml-auto inline-flex min-h-[2.5rem] items-center gap-1.5 rounded-lg border-2 border-teal-300 bg-white px-3 py-1 text-sm font-semibold text-teal-900 hover:bg-teal-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 disabled:opacity-60 lg:ml-0"
                  >
                    <Ikon navn="dokument" className="w-4 h-4" />
                    Plukkliste<span className="sr-only"> for skiplukk {o.ordreid}, skriv ut</span>
                  </button>
                </div>
                <Detaljer id={detaljId} o={o} par={par} />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
